import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileText, Key, BookOpen, Folder, UploadCloud, Download, Trash2, Plus, X, Eye, ArrowLeft,
} from "lucide-react";
import api, { formatErr, fileUrl, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { confirmDialog } from "../lib/confirm";
import { BRAND_COLORS } from "../lib/bookingStatus";
import { deDateMedium } from "../lib/dateFormat";

const MAX_MB = 25;

function useCategories(de) {
  return [
    { key: "contract", label: de ? "Verträge"    : "Contracts", icon: FileText },
    { key: "key",      label: de ? "Schlüssel"   : "Keys",      icon: Key },
    { key: "manual",   label: de ? "Handbücher"  : "Manuals",   icon: BookOpen },
    { key: "other",    label: de ? "Sonstiges"   : "Other",     icon: Folder },
  ];
}

function bytesFmt(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function UploadModal({ open, onClose, onSaved, de }) {
  const CATEGORIES = useCategories(de);
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("contract");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setTitle(""); setCategory("contract"); setNotes(""); if (fileRef.current) fileRef.current.value = ""; };
  const close = () => { reset(); onClose(); };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(de ? `Datei zu groß (max. ${MAX_MB} MB).` : `File too large (max. ${MAX_MB} MB).`);
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const save = async () => {
    if (!file) { toast.error(de ? "Bitte eine Datei wählen." : "Please choose a file."); return; }
    if (!title.trim()) { toast.error(de ? "Bitte einen Titel angeben." : "Please provide a title."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: fref } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: doc } = await api.post("/documents", {
        file_id: fref.id,
        title: title.trim(),
        category,
        notes: notes.trim() || null,
      });
      toast.success(de ? "Dokument hinzugefügt." : "Document added.");
      onSaved(doc);
      close();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-navy/70 p-0 md:p-6" onClick={close} data-testid="doc-upload-modal">
      <div className="bg-white w-full md:max-w-lg md:rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy/10">
          <div>
            <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>
              {de ? "Dokument hinzufügen" : "Add document"}
            </div>
            <h2 className="font-display text-2xl text-navy mt-1">
              {de ? "Neues Dokument" : "New document"}
            </h2>
          </div>
          <button onClick={close} data-testid="doc-upload-close" aria-label={de ? "Schließen" : "Close"}>
            <X className="w-5 h-5 text-navy" />
          </button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <div className="label-caps mb-2">{de ? "Datei" : "File"}</div>
            {file ? (
              <div className="flex items-center gap-3 border border-gold/40 bg-ivory/60 px-3 py-3" data-testid="doc-upload-file-chip">
                <FileText className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-navy truncate">{file.name}</div>
                  <div className="text-[11px] text-navy-dark/60">{bytesFmt(file.size)}</div>
                </div>
                <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-navy-dark/60 hover:text-navy" aria-label={de ? "Datei entfernen" : "Remove file"}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label htmlFor="doc-upload-input"
                className="flex flex-col items-center justify-center gap-2 border border-dashed border-navy/25 hover:border-gold cursor-pointer bg-ivory/40 py-8"
                data-testid="doc-upload-picker">
                <UploadCloud className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
                <div className="label-caps">{de ? "Datei wählen" : "Choose file"}</div>
                <div className="text-[11px] text-navy-dark/50 italic">PDF, JPG, PNG, DOC · max. {MAX_MB} MB</div>
                <input id="doc-upload-input" ref={fileRef} type="file" className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt" onChange={onPick}
                  data-testid="doc-upload-file-input" />
              </label>
            )}
          </div>
          <div>
            <label htmlFor="doc-title" className="label-caps block mb-2">{de ? "Titel" : "Title"}</label>
            <input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={de ? "z. B. Mietvertrag 2026" : "e.g. Lease agreement 2026"}
              data-testid="doc-title-input"
              className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark" />
          </div>
          <div>
            <div className="label-caps mb-2">{de ? "Kategorie" : "Category"}</div>
            <div className="grid grid-cols-4 gap-2" data-testid="doc-category-picker">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const active = category === c.key;
                return (
                  <button key={c.key} type="button" data-testid={`doc-cat-${c.key}`}
                    onClick={() => setCategory(c.key)}
                    className={`flex flex-col items-center gap-1.5 py-3 border transition-colors ${active ? "border-gold bg-gold/5" : "border-navy/15 hover:border-navy/40"}`}>
                    <Icon className={`w-4 h-4 ${active ? "text-navy" : "text-navy-dark/60"}`} strokeWidth={1.5} />
                    <div className={`text-[11px] tracking-wider ${active ? "text-navy" : "text-navy-dark/70"}`}>{c.label}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label htmlFor="doc-notes" className="label-caps block mb-2">
              {de ? "Notiz" : "Note"} <span className="lowercase text-navy-dark/50 italic">({de ? "optional" : "optional"})</span>
            </label>
            <textarea id="doc-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder={de
                ? "z. B. gültig bis 12/2027 · Standort Schlüsselkasten …"
                : "e.g. valid until 12/2027 · key-box location …"}
              data-testid="doc-notes-input"
              className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark leading-relaxed" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-navy/10 flex justify-end gap-3">
          <button onClick={close} disabled={busy} className="text-[12px] tracking-wider uppercase text-navy-dark/70 hover:text-navy px-4 py-2.5">
            {de ? "Abbrechen" : "Cancel"}
          </button>
          <button onClick={save} disabled={busy} data-testid="doc-upload-save-btn"
            className="text-ivory px-6 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{ background: BRAND_COLORS.navy }}>
            {busy ? (de ? "Wird gespeichert…" : "Saving…") : (de ? "Speichern" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function DocRow({ doc, onDeleted, onOpen, de, categories }) {
  const CAT_MAP = Object.fromEntries(categories.map((c) => [c.key, c]));
  const cat = CAT_MAP[doc.category] || CAT_MAP.other;
  const Icon = cat.icon;
  const [deleting, setDeleting] = useState(false);
  const del = async () => {
    if (!(await confirmDialog({
      title: de ? "Dokument löschen?" : "Delete document?",
      description: de
        ? `„${doc.title}" wird unwiderruflich entfernt.`
        : `“${doc.title}” will be permanently removed.`,
      confirmText: de ? "Löschen" : "Delete",
      variant: "danger",
    }))) return;
    setDeleting(true);
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success(de ? "Dokument gelöscht." : "Document deleted.");
      onDeleted(doc.id);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      setDeleting(false);
    }
  };
  return (
    <li className="bg-white border border-navy/10 p-4 flex items-center gap-3" data-testid={`doc-row-${doc.id}`}>
      <button
        type="button"
        onClick={() => onOpen(doc)}
        data-testid={`doc-open-${doc.id}`}
        className="flex-1 min-w-0 flex items-center gap-3 text-left group"
      >
        <span className="w-10 h-10 rounded-full bg-ivory flex items-center justify-center shrink-0 group-hover:bg-gold/15 transition-colors">
          <Icon className="w-4 h-4 text-navy-dark/70" strokeWidth={1.5} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[14px] text-navy font-medium truncate group-hover:text-gold transition-colors">{doc.title}</span>
          <span className="block text-[11.5px] text-navy-dark/55 truncate">
            {deDateMedium(doc.created_at)}
            {doc.notes ? ` · ${doc.notes}` : ""}
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={() => onOpen(doc)}
        data-testid={`doc-view-${doc.id}`}
        className="w-9 h-9 flex items-center justify-center border border-navy/15 hover:border-gold transition-colors"
        title={de ? "Öffnen" : "Open"}
      >
        <Eye className="w-4 h-4 text-navy" strokeWidth={1.5} />
      </button>
      <button
        onClick={del}
        disabled={deleting}
        data-testid={`doc-delete-${doc.id}`}
        className="w-9 h-9 flex items-center justify-center border border-navy/15 hover:border-red-600 hover:text-red-600 transition-colors text-navy-dark/70"
        title={de ? "Löschen" : "Delete"}
      >
        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </li>
  );
}

function DocViewerModal({ doc, onClose, de }) {
  if (!doc) return null;
  const url = fileUrl(doc.file_id);
  const ext = (doc.original_filename || doc.title || "").split(".").pop().toLowerCase();
  const isPdf = ext === "pdf";
  const isImg = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "heic", "heif", "svg"].includes(ext);
  return (
    <div className="fixed inset-0 z-[60] flex flex-col" style={{ background: BRAND_COLORS.navyDark }} data-testid="doc-viewer-modal">
      <div className="flex items-center gap-3 px-4 py-3 shrink-0" style={{ background: BRAND_COLORS.navy }}>
        <button
          type="button"
          onClick={onClose}
          data-testid="doc-viewer-back"
          className="inline-flex items-center gap-2 text-ivory/90 hover:text-ivory text-[12px] tracking-[0.16em] uppercase transition-colors"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.6} />
          {de ? "Zurück" : "Back"}
        </button>
        <div className="flex-1 min-w-0 text-center px-2">
          <div className="text-[13px] text-ivory truncate">{doc.title}</div>
        </div>
        <a
          href={url}
          download={doc.original_filename || doc.title}
          target="_blank"
          rel="noreferrer"
          data-testid="doc-viewer-download"
          className="w-9 h-9 flex items-center justify-center text-ivory/90 hover:text-ivory transition-colors"
          title={de ? "Herunterladen" : "Download"}
        >
          <Download className="w-5 h-5" strokeWidth={1.6} />
        </a>
        <button
          type="button"
          onClick={onClose}
          data-testid="doc-viewer-close"
          aria-label={de ? "Schließen" : "Close"}
          className="w-9 h-9 flex items-center justify-center text-ivory/90 hover:text-ivory transition-colors"
        >
          <X className="w-5 h-5" strokeWidth={1.6} />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto flex items-center justify-center">
        {isPdf ? (
          <iframe src={url} title={doc.title} className="w-full h-full border-0 bg-white" data-testid="doc-viewer-iframe" />
        ) : isImg ? (
          <img src={url} alt={doc.title} className="max-w-full max-h-full object-contain" data-testid="doc-viewer-image" />
        ) : (
          <div className="text-center p-8" data-testid="doc-viewer-fallback">
            <FileText className="w-10 h-10 mx-auto mb-4 text-ivory/70" strokeWidth={1.3} />
            <p className="text-ivory/80 text-[14px] mb-6 max-w-xs mx-auto leading-relaxed">
              {de
                ? "Dieses Dateiformat kann nicht direkt angezeigt werden. Laden Sie es herunter, um es zu öffnen."
                : "This file type can't be previewed directly. Download it to open."}
            </p>
            <a
              href={url}
              download={doc.original_filename || doc.title}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-navy bg-ivory px-6 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 transition-opacity"
            >
              <Download className="w-4 h-4" strokeWidth={1.6} />
              {de ? "Herunterladen" : "Download"}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CustomerDocuments() {
  const { lang } = useI18n();
  const de = lang === "de";
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const CATEGORIES = useCategories(de);
  const [docs, setDocs] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/documents");
        setDocs(data);
      } catch (e) {
        console.error("documents load failed:", e);
        setDocs([]);
      }
    })();
    // 25 Sep 2026 · Öffnen der Seite quittiert neue Dokumente → Neuigkeiten-Badge sinkt.
    api.post("/documents/mark-read").then(() => {
      window.dispatchEvent(new Event("weissblom:unread-refresh"));
    }).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    if (!docs) return {};
    const g = {};
    for (const d of docs) (g[d.category] = g[d.category] || []).push(d);
    return g;
  }, [docs]);

  const filtered = useMemo(() => {
    if (!docs) return [];
    return filter === "all" ? docs : (grouped[filter] || []);
  }, [docs, filter, grouped]);

  return (
    <div className="max-w-[900px] mx-auto px-5 md:px-10 py-8 md:py-14 pb-32" data-testid="customer-documents">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="label-caps mb-3">{de ? "Ihr Objekt" : "Your property"}</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy leading-tight">
            {de ? "Dokumente" : "Documents"}
          </h1>
          <p className="text-[14px] text-navy-dark/70 mt-3 max-w-lg">
            {de
              ? "Verträge, Schlüsselcodes und Handbücher an einem Ort. Jederzeit abrufbar."
              : "Contracts, key codes and manuals in one place. Available anytime."}
          </p>
        </div>
        <button
          onClick={() => setUploadOpen(true)}
          data-testid="doc-add-btn"
          className="shrink-0 w-11 h-11 md:w-auto md:h-auto md:px-5 md:py-3 flex items-center justify-center gap-2 text-ivory text-[11px] tracking-[0.18em] uppercase hover:opacity-90"
          style={{ background: BRAND_COLORS.navy }}
          aria-label={de ? "Neues Dokument" : "New document"}
        >
          <Plus className="w-5 h-5" strokeWidth={1.6} />
          <span className="hidden md:inline">{de ? "Neu" : "New"}</span>
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-1 px-1" data-testid="doc-filter-chips">
        <button onClick={() => setFilter("all")} data-testid="doc-filter-all"
          className={`whitespace-nowrap px-4 py-2 text-[12px] tracking-wider border ${filter === "all" ? "border-navy bg-navy text-ivory" : "border-navy/20 text-navy-dark hover:border-navy/50"}`}
          style={filter === "all" ? { background: BRAND_COLORS.navy } : {}}>
          {de ? "Alle" : "All"} {docs ? `(${docs.length})` : ""}
        </button>
        {CATEGORIES.map((c) => {
          const count = (grouped[c.key] || []).length;
          const Icon = c.icon;
          return (
            <button key={c.key} onClick={() => setFilter(c.key)} data-testid={`doc-filter-${c.key}`}
              className={`inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 text-[12px] tracking-wider border ${filter === c.key ? "border-navy bg-navy text-ivory" : "border-navy/20 text-navy-dark hover:border-navy/50"}`}
              style={filter === c.key ? { background: BRAND_COLORS.navy } : {}}>
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {c.label} {count > 0 && <span className={filter === c.key ? "text-ivory/70" : "text-navy-dark/50"}>· {count}</span>}
            </button>
          );
        })}
      </div>

      {docs === null ? (
        <div className="bg-white border border-navy/10 p-8 text-center label-caps">
          {de ? "Wird geladen …" : "Loading …"}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-ivory/60 border border-dashed border-navy/20 p-10 text-center" data-testid="documents-empty">
          <Folder className="w-8 h-8 mx-auto mb-3" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.3} />
          <div className="font-display text-xl text-navy">
            {de ? "Noch keine Dokumente" : "No documents yet"}
          </div>
          <p className="text-[13px] text-navy-dark/60 mt-2 max-w-xs mx-auto">
            {de
              ? "Laden Sie Verträge, Schlüsselcodes oder Handbücher hoch — Ihr Concierge-Team ergänzt weitere für Sie."
              : "Upload contracts, key codes or manuals — your concierge team will add more for you."}
          </p>
          <button onClick={() => setUploadOpen(true)} data-testid="documents-empty-cta"
            className="mt-6 inline-flex items-center gap-2 text-ivory px-6 py-3 text-[11px] tracking-[0.18em] uppercase"
            style={{ background: BRAND_COLORS.navy }}>
            <Plus className="w-4 h-4" strokeWidth={1.6} /> {de ? "Erstes Dokument" : "First document"}
          </button>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="documents-list">
          {filtered.map((d) => (
            <DocRow key={d.id} doc={d} de={de} categories={CATEGORIES}
              onOpen={setViewDoc}
              onDeleted={(id) => setDocs((cur) => cur.filter((x) => x.id !== id))} />
          ))}
        </ul>
      )}

      <UploadModal open={uploadOpen} de={de} onClose={() => setUploadOpen(false)}
        onSaved={(doc) => setDocs((cur) => [doc, ...(cur || [])])} />

      <DocViewerModal doc={viewDoc} de={de} onClose={() => setViewDoc(null)} />

      <span className="hidden" data-api-base={API} />
    </div>
  );
}
